import { useMemo, useState } from 'react';
import { Box, Button, Group, IconPlusCircle, Overlay, Typography, Wrapper } from '@screentone/core';
import { v4 as uuidv4 } from 'uuid';

import { EntityType } from 'contexts/datamodel/DataModelContext';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import {
  PageModule,
  PublicationSetting,
  PublicationSettingSearchableContentType,
  QueryItem
} from 'data/generated/graphql';
import { QueryModal } from 'features/section-page-edit/components/page-draft-section/components/query-modal/QueryModal';
import { usePublicationSettings } from 'hooks/publication-settings';
import { BaseQueryOrigin, getBaseQuery } from 'utils/contentType';
import { mergeAllessehQueryBodies } from 'utils/queryUtils';
import styles from './AddQuery.module.scss';
import { getDefaultQuery, QueryOption } from './queryUtil';

interface AddQueryModalProps {
  count: number;
  hierarchyId: string;
  queryOptions: QueryOption[];
  maxAllowedItems: number;
  numTotalItemsUsed: number;
  ignoreBaseQuery?: boolean;
  onClose: () => void;
  pageModule?: PageModule;
  onPageModuleChange: (newPageModule: PageModule) => void;
  defaultQuery: QueryOption;
}

const AddQueryModal = ({
  count,
  hierarchyId,
  queryOptions,
  maxAllowedItems,
  numTotalItemsUsed,
  onClose,
  pageModule,
  onPageModuleChange,
  defaultQuery,
  ignoreBaseQuery = false,
}: AddQueryModalProps) => {
  const { metadata, insertEntity, modifyEntity, applyOverfetchForAddedQuery } = useDataModelContext();
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);
  const [isNewQuery, setIsNewQuery] = useState(false);

  const { data: { publicationSetting } = {} as { publicationSetting: PublicationSetting } } = usePublicationSettings();
  const uuid = useMemo(() => uuidv4(), []);
  const queryId = useMemo(() => uuidv4(), []);

  const { publicationKey } = metadata;

  const modifiedDefaultQuery = {
    ...defaultQuery,
    query: {
      ...defaultQuery.query,
      count
    }
  };

  const baseQuery = getBaseQuery({
    contentTypes: [PublicationSettingSearchableContentType.Article],
    publicationSettings: publicationSetting,
    origin: BaseQueryOrigin.QUERY_BUILDER
  });

  const handleClose = () => {
    setIsModalOpen(false);
    setIsQueryModalOpen(false);
    onClose();
  };

  const updatePageModule = (updatedPageModule: PageModule | null) => {
    const basePageModule = updatedPageModule ?? pageModule;

    if (basePageModule) {
      onPageModuleChange(basePageModule);
    }
  };

  const handleAddQuery = (option: QueryOption) => {
    const query = option.query as Record<string, unknown>;
    const queryItem: QueryItem = {
      type: EntityType.Query,
      uuid,
      attributes: { id: queryId, query, feedTitle: option.label }
    };
    insertEntity(hierarchyId, queryItem);

    // apply overfetch if this query is a duplicate and get the updated pageModule
    const moduleHierarchyId = hierarchyId.substring(0, hierarchyId.lastIndexOf('-'));

    let updatedPageModule: PageModule | null = null;
    if (moduleHierarchyId) {
      updatedPageModule = applyOverfetchForAddedQuery(moduleHierarchyId, query);
    }

    updatePageModule(updatedPageModule);
    handleClose();
  };

  const onCreateUniqueQuery = () => {
    const queryItem: QueryItem = {
      type: EntityType.Query,
      uuid,
      attributes: {
        id: queryId,
        query: modifiedDefaultQuery.query as Record<string, unknown>,
        feedTitle: defaultQuery.label
      }
    };

    insertEntity(hierarchyId, queryItem);
    setIsModalOpen(false);
    setIsQueryModalOpen(true);
    setIsNewQuery(false);
  };

  const handleChangeJsonQueryStr = (newJsonQueryStr: string) => {
    const moduleHierarchyId = hierarchyId.substring(0, hierarchyId.lastIndexOf('-'));

    if (isNewQuery) {
      const defaultQueryBrand = getDefaultQuery({ publicationKey, publicationSetting });
      const query = JSON.parse(newJsonQueryStr) as Record<string, unknown>;
      const queryItem: QueryItem = {
        type: EntityType.Query,
        uuid,
        attributes: {
          id: queryId,
          query,
          feedTitle: defaultQueryBrand.label
        }
      };
      insertEntity(hierarchyId, queryItem);

      if (moduleHierarchyId) {
        applyOverfetchForAddedQuery(moduleHierarchyId, query);
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const finalQuery = ignoreBaseQuery
        ? mergeAllessehQueryBodies(JSON.stringify(baseQuery), newJsonQueryStr)
        : JSON.parse(newJsonQueryStr);

      modifyEntity(hierarchyId, (entity) => {
        const query = entity as QueryItem;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        query.attributes.query = finalQuery;
      });

      if (moduleHierarchyId) {
        applyOverfetchForAddedQuery(moduleHierarchyId, finalQuery as Record<string, unknown>);
      }
    }
    handleClose();
  };

  return (
    <>
      {isModalOpen && (
        <Overlay status="open" onDismiss={handleClose}>
          <Box>
            <Box.Title>ADD QUERY</Box.Title>
            <Box.Content padding={{ all: 'none' }}>
              <Group gap="sm">
                <Wrapper padding={{ all: 'md' }} className={styles.content}>
                  <Wrapper className={styles.contentLeft}>
                    <Typography variant="label3" margin={{ bottom: 'md' }}>
                      Query Presets
                    </Typography>
                    <div className={styles.contentList}>
                      {queryOptions.map((option, i) => (
                        <Button
                          key={i}
                          icon={IconPlusCircle}
                          tertiary
                          onClick={() => handleAddQuery(option)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </Wrapper>
                  <Wrapper className={styles.contentRight}>
                    <Button secondary onClick={onCreateUniqueQuery}>
                      Create unique query
                    </Button>
                  </Wrapper>
                </Wrapper>
              </Group>
            </Box.Content>
          </Box>
        </Overlay>
      )}
      {isQueryModalOpen && (
        <QueryModal
          queryId={queryId}
          queryItemHierarchyId={hierarchyId}
          allessehJsonQuery={JSON.stringify(modifiedDefaultQuery.query)}
          onDismiss={handleClose}
          onChangeJsonQueryStr={handleChangeJsonQueryStr}
          count={maxAllowedItems - numTotalItemsUsed}
          baseQuery={baseQuery}
          ignoreBaseQuery={ignoreBaseQuery}
        />
      )}
    </>
  );
};

export default AddQueryModal;
